import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiHelpCircle } from 'react-icons/fi';

const FAQSection = styled.section`
  background: #fff;
  padding: 100px 20px;
  width: 100%;
`;

const Inner = styled.div`
  max-width: 740px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #DA015C;
  margin-bottom: 16px;
  text-align: center;
`;

const Title = styled.h2`
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 900;
  color: #0a174e;
  text-align: center;
  letter-spacing: -0.03em;
  line-height: 1.1;
  margin-bottom: 12px;
  span {
    color: #DA015C;
  }
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: #5a6a7c;
  text-align: center;
  line-height: 1.75;
  margin-bottom: 56px;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FAQItem = styled.div<{ open: boolean }>`
  background: ${p => p.open ? '#0a174e' : '#f8fafb'};
  border-radius: 16px;
  border: 1px solid ${p => p.open ? 'transparent' : 'rgba(10,23,78,0.06)'};
  overflow: hidden;
  transition: background 0.3s ease, box-shadow 0.3s ease;
  box-shadow: ${p => p.open ? '0 8px 32px rgba(10,23,78,0.18)' : '0 2px 8px rgba(10,23,78,0.03)'};
`;

const FAQQuestion = styled.button<{ open: boolean }>`
  width: 100%;
  background: none;
  border: none;
  outline: none;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${p => p.open ? '#fff' : '#0a174e'};
  padding: 22px 24px;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  transition: color 0.25s;
`;

const ChevronIcon = styled(motion.span)<{ open: boolean }>`
  flex-shrink: 0;
  display: flex;
  color: ${p => p.open ? '#DA015C' : '#5a6a7c'};
  font-size: 1.2rem;
`;

const FAQAnswer = styled(motion.div)`
  padding: 0 24px 22px 24px;
  font-size: 0.98rem;
  color: rgba(255,255,255,0.75);
  line-height: 1.8;
  overflow: hidden;
`;

const faqs = [
  {
    q: "How do I sign up as a rider?",
    a: "It's easy — just join the waitlist above with your phone number and city. We'll send you an invite as soon as Wassel launches near you. No app download required until then."
  },
  {
    q: "Where is Wassel launching?",
    a: "We're starting in underserved cities that have been overlooked by mainstream ride-hailing platforms — across the Middle East, North Africa, and beyond. More cities are being added every month. Join the waitlist and tell us your city!"
  },
  {
    q: "How much does a ride cost?",
    a: "Wassel is built on transparent, fair pricing. You'll always see the exact fare before confirming your ride — no surge pricing, no hidden fees, no unpleasant surprises at the end of your trip."
  },
  {
    q: "Is Wassel safe?",
    a: "Safety is our top priority. All drivers go through thorough background checks and professional training. Every ride is GPS-tracked in real-time, and you can share your trip status with family or friends at any time."
  },
  {
    q: "Can I book a ride for someone else?",
    a: "Absolutely. You can request rides for family members, friends, or colleagues directly from your account — even if they don't have the app themselves."
  },
  {
    q: "What makes Wassel different from other ride apps?",
    a: "Wassel is purpose-built for cities and communities that have been underserved by global platforms. We prioritize local needs, fair driver earnings, transparent pricing, and deep community trust — not just growth metrics."
  }
];

const DriverFAQEn: React.FC = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <FAQSection id="faq">
      <Inner>
        <Eyebrow>Got Questions?</Eyebrow>
        <Title>Frequently <span>Asked Questions</span></Title>
        <Subtitle>Everything you need to know before your first ride with Wassel.</Subtitle>
        <FAQList>
          {faqs.map((item, idx) => {
            const isOpen = openIdx === idx;
            return (
              <FAQItem key={idx} open={isOpen}>
                <FAQQuestion
                  open={isOpen}
                  onClick={() => setOpenIdx(isOpen ? null : idx)}
                  aria-expanded={isOpen}
                >
                  {item.q}
                  <ChevronIcon
                    open={isOpen}
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <FiChevronDown />
                  </ChevronIcon>
                </FAQQuestion>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <FAQAnswer
                      key="answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      {item.a}
                    </FAQAnswer>
                  )}
                </AnimatePresence>
              </FAQItem>
            );
          })}
        </FAQList>
      </Inner>
    </FAQSection>
  );
};

export default DriverFAQEn;
